/**
 * JobMapSection: the team's task map with each task's share of traffic
 * (TRA-1513). Staff only until customers can edit it. The code says
 * "job"; everything a person reads says "task".
 *
 * Self-contained on purpose: it takes nothing from Settings but its
 * registry entry for the header, so it can move to a sidebar page later.
 * The window (7 or 30 days) is local state; the API counts the rest.
 */

import { useState } from 'react'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type { JobMapReport } from '@/api/generated/model'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock } from '@/components/settings/SettingsSection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useJobMap, type JobMapWindow } from '@/hooks/useTeamSettings'

import { countLine } from './jobGroups'
import { JobMapTable } from './JobMapTable'

const WINDOWS: readonly JobMapWindow[] = [7, 30]
/** The page is one font: over the badge's and the button's capitals in the code font. */
const PLAIN = 'font-sans normal-case tracking-normal'

export function JobMapSection({ section }: SettingsSectionProps): React.JSX.Element {
  const [days, setDays] = useState<JobMapWindow>(30)
  const report = useJobMap(days)

  return (
    <div>
      {/* No description on this page, so the count line sits close under the title. */}
      <SettingsPageHeader
        section={section}
        className="pb-2"
        badge={
          <Badge variant="secondary" className={PLAIN}>
            Staff only
          </Badge>
        }
      />
      {report.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : report.data === undefined && report.isError ? (
        <SettingsBlock>
          <p className="text-sm text-muted-foreground">
            {getApiErrorStatus(report.error) === 404
              ? 'This team has no task map yet.'
              : (getApiErrorDetail(report.error) ?? 'Failed to load the task map.')}
          </p>
        </SettingsBlock>
      ) : report.data ? (
        <JobMapReportView report={report.data} days={days} onDays={setDays} />
      ) : null}
    </div>
  )
}

interface JobMapReportViewProps {
  report: JobMapReport
  days: JobMapWindow
  onDays: (days: JobMapWindow) => void
}

function JobMapReportView({ report, days, onDays }: JobMapReportViewProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{countLine(report)}</p>
        <div className="flex gap-1" role="group" aria-label="Window">
          {WINDOWS.map((window) => (
            <Button
              key={window}
              type="button"
              size="sm"
              variant={window === days ? 'default' : 'outline'}
              className={PLAIN}
              aria-pressed={window === days}
              onClick={() => {
                onDays(window)
              }}
            >
              {window} days
            </Button>
          ))}
        </div>
      </div>

      <JobMapTable report={report} />
    </div>
  )
}
